import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import type { GscCallbackResult } from '@traaker/shared';
import { searchConsoleApi } from '../services/search-console';

/**
 * Where Google redirects after the Search Console consent (lot H step 7). Inside
 * ProtectedRoute: the `{ code, state }` pair is POSTed to an AUTHENTICATED API route,
 * which binds the grant to the logged-in user. The code and state are dropped from the
 * URL before the POST (no code in history or a Referer) and never logged.
 */
export default function SearchConsoleCallback() {
  const navigate = useNavigate();
  // StrictMode runs the effect twice on the same instance: a second POST would read `expired`.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Prod: the inline head script (vite.config.ts gtmPlugin) already moved the params out
    // of the URL before GTM loaded; dev has no GTM and no stash.
    const w = window as { __gscCallbackSearch?: string };
    const search = w.__gscCallbackSearch ?? window.location.search;
    delete w.__gscCallbackSearch;
    void completeGscCallback({
      search,
      origin: window.location.origin,
      strip: () => window.history.replaceState(null, '', window.location.pathname),
      post: searchConsoleApi.callback,
    }).then((to) => navigate(to, { replace: true }));
  }, [navigate]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

export interface GscCallbackIo {
  /** `window.location.search` as Google left it. */
  search: string;
  origin: string;
  /** Drops every query param from the address bar (called BEFORE the POST). */
  strip: () => void;
  post: (body: { code?: string; state: string; error?: string }) => Promise<{ result: GscCallbackResult; returnPath: string }>;
}

/** Reads Google's params, strips them, POSTs them, and returns where to navigate. */
export async function completeGscCallback(io: GscCallbackIo): Promise<string> {
  const p = new URLSearchParams(io.search);
  const code = p.get('code') ?? undefined;
  const state = p.get('state') ?? undefined;
  const error = p.get('error') ?? undefined;
  io.strip();
  if (!state) return '/?gsc=error';
  try {
    const res = await io.post({ ...(code ? { code } : {}), state, ...(error ? { error } : {}) });
    return gscReturnTarget(res.returnPath, res.result, io.origin);
  } catch {
    return '/?gsc=error';
  }
}

/**
 * The return URL (step 5): never a concatenated `?gsc=`. A returnPath with its own
 * query string keeps it; anything that resolves to another origin goes to `/`.
 */
export function gscReturnTarget(returnPath: string, result: GscCallbackResult, origin: string): string {
  let u: URL;
  try {
    u = new URL(returnPath, origin);
  } catch {
    return '/';
  }
  if (u.origin !== origin) return '/';
  u.searchParams.set('gsc', result);
  return u.pathname + u.search + u.hash;
}
